import { useState } from "react";
import { redirect, useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { currentUser, signIn } from "@/services/auth";

/**
 * Only same-origin paths are honoured, so `?next=` cannot be an open redirect.
 *
 * A leading `//` is rejected as well as a scheme: `//evil.example.com` is a
 * protocol-relative URL, and a browser follows it off-site.
 */
export function safeNext(value: string | null): string {
  const path = value ?? "";
  return path.startsWith("/") && !path.startsWith("//") ? path : "/inbox";
}

/** Someone already signed in has no business on the login screen. */
export async function loginLoader() {
  const user = await currentUser();
  return user ? redirect("/inbox") : null;
}

export function LoginPage() {
  useDocumentTitle("Sign in");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const next = safeNext(params.get("next"));

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    // A FormData entry is a string or a File; these inputs are text, but
    // stringifying a File would post "[object File]" as a password.
    const field = (name: string): string => {
      const value = form.get(name);
      return typeof value === "string" ? value : "";
    };
    setPending(true);
    setError(null);

    const result = await signIn(field("email"), field("password"));
    if (result.ok) {
      // A full navigation, so every loader re-runs against the new session.
      await navigate(next, { replace: true });
      return;
    }
    setError(result.error);
    setPending(false);
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="grid size-7 place-items-center rounded bg-accent text-xs font-bold text-white">
              CU
            </span>
            <span className="text-sm font-semibold tracking-tight">Credit Underwriting</span>
          </div>
          <p className="mt-3 text-xs text-ink-muted">
            Sign in to reach your inbox and open cases.
          </p>
        </div>

        <div className="rounded-lg border border-line bg-surface p-5">
          <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
            <label className="block">
              <span className="eyebrow">Email</span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
              />
            </label>

            <label className="block">
              <span className="eyebrow">Password</span>
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="mt-1 w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
              />
            </label>

            {error ? (
              <p
                role="alert"
                className="rounded-md border border-negative/20 bg-negative-soft px-3 py-2 text-xs text-negative"
              >
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" className="w-full" disabled={pending}>
              {pending ? "Signing in…" : "Sign in"}
            </Button>

            <p className="text-center text-xs text-ink-faint">
              Authenticated by the platform. Your password never reaches this page&rsquo;s
              JavaScript beyond this form.
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}
